import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { arrayMove } from '@dnd-kit/sortable';
import api from '../utils/api';
import {
  ROUTES_API_MAX_INTERMEDIATES,
  getOrderId,
  isValidLatLng,
} from '../constants/routePlanning';

const ESTIMATE_DEBOUNCE_MS = 400;

/**
 * Single source of truth for route-building state and API calls.
 * Consumed by OrderMapSelector (map path) and RiderAllocationModal (checkbox path)
 * so the two entry points cannot produce different optimize/create results (R9).
 */
export default function useRouteBuilder({ onMessage, autoEstimate = true } = {}) {
  const [orderedIds, setOrderedIds] = useState([]);
  const [ordersById, setOrdersById] = useState({});
  const [missingLocationOrders, setMissingLocationOrders] = useState([]);
  const [returnToStore, setReturnToStoreState] = useState(true);
  const [totalDistanceMeters, setTotalDistanceMeters] = useState(null);
  const [totalDurationSeconds, setTotalDurationSeconds] = useState(null);
  const [originStoreId, setOriginStoreId] = useState(null);
  const [storeIds, setStoreIds] = useState([]);
  const [multiStore, setMultiStore] = useState(false);
  const [sequenceSource, setSequenceSource] = useState('manual');
  const [estimating, setEstimating] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [optimizeDisabled, setOptimizeDisabled] = useState(false);
  const [optimizeDisabledReason, setOptimizeDisabledReason] = useState('');
  const [createdRoute, setCreatedRoute] = useState(null);
  const [moveDeltas, setMoveDeltas] = useState({});
  const [estimateError, setEstimateError] = useState('');

  const estimateTimerRef = useRef(null);
  const estimateRequestIdRef = useRef(0);
  // Set after a successful optimize so the orderedIds change does not fire a redundant estimate.
  const skipNextEstimateRef = useRef(false);
  const notify = useCallback(
    (message, severity = 'info') => {
      if (typeof onMessage === 'function') onMessage(message, severity);
    },
    [onMessage]
  );

  const clearTotals = useCallback(() => {
    setTotalDistanceMeters(null);
    setTotalDurationSeconds(null);
    setOriginStoreId(null);
    setStoreIds([]);
    setMultiStore(false);
    setEstimateError('');
  }, []);

  const reset = useCallback(() => {
    estimateRequestIdRef.current += 1;
    if (estimateTimerRef.current) {
      clearTimeout(estimateTimerRef.current);
      estimateTimerRef.current = null;
    }
    setOrderedIds([]);
    setOrdersById({});
    setMissingLocationOrders([]);
    setReturnToStoreState(true);
    clearTotals();
    setSequenceSource('manual');
    setEstimating(false);
    setOptimizing(false);
    setCreating(false);
    setOptimizeDisabled(false);
    setOptimizeDisabledReason('');
    setCreatedRoute(null);
    setMoveDeltas({});
  }, [clearTotals]);

  /**
   * Map path entry: full order objects already in hand (from mode=map fetch).
   * Pins are plotted by the map from these orders; the route panel starts EMPTY —
   * nothing is auto-added. Missing/{0,0} coords are excluded from plotting and
   * surfaced separately (R4).
   */
  const initFromOrders = useCallback((orders = []) => {
    const byId = {};
    const missing = [];

    orders.forEach((order) => {
      const id = getOrderId(order);
      if (!id) return;
      byId[id] = order;
      if (!isValidLatLng(order.deliveryAddress?.latLong)) {
        missing.push(order);
      }
    });

    setOrdersById(byId);
    setMissingLocationOrders(missing);
    setOrderedIds([]);
    setSequenceSource('manual');
    setOptimizeDisabled(false);
    setOptimizeDisabledReason('');
    setCreatedRoute(null);
    setMoveDeltas({});
    clearTotals();
  }, [clearTotals]);

  /**
   * Checkbox path entry: order ID list only. Coords are resolved server-side
   * on estimate/optimize; the modal may still fetch details for its own table UI.
   */
  const initFromOrderIds = useCallback((orderIds = []) => {
    const ids = orderIds.filter(Boolean);
    setOrdersById({});
    setMissingLocationOrders([]);
    setOrderedIds(ids);
    setSequenceSource('manual');
    setOptimizeDisabled(ids.length > ROUTES_API_MAX_INTERMEDIATES);
    setOptimizeDisabledReason(
      ids.length > ROUTES_API_MAX_INTERMEDIATES
        ? `Optimize is limited to ${ROUTES_API_MAX_INTERMEDIATES} stops (Google Routes API). You can still create the route with this manual sequence.`
        : ''
    );
    setCreatedRoute(null);
    setMoveDeltas({});
    clearTotals();
  }, [clearTotals]);

  const setReturnToStore = useCallback((value) => {
    setReturnToStoreState(Boolean(value));
    setSequenceSource('manual');
  }, []);

  const addOrder = useCallback((orderOrId) => {
    const order = typeof orderOrId === 'object' ? orderOrId : null;
    const id = order ? getOrderId(order) : orderOrId;
    if (!id) return;
    if (order) {
      setOrdersById((prev) => ({ ...prev, [id]: order }));
    }
    setOrderedIds((prev) => {
      if (prev.includes(id)) return prev;
      const next = [...prev, id];
      if (next.length > ROUTES_API_MAX_INTERMEDIATES) {
        setOptimizeDisabled(true);
        setOptimizeDisabledReason(
          `Optimize is limited to ${ROUTES_API_MAX_INTERMEDIATES} stops (Google Routes API). You can still create the route with this manual sequence.`
        );
      }
      return next;
    });
    setSequenceSource('manual');
  }, []);

  const removeOrder = useCallback((orderId) => {
    setOrderedIds((prev) => {
      const next = prev.filter((id) => id !== orderId);
      if (next.length <= ROUTES_API_MAX_INTERMEDIATES) {
        setOptimizeDisabled(false);
        setOptimizeDisabledReason('');
      }
      return next;
    });
    setSequenceSource('manual');
  }, []);

  const reorder = useCallback((activeId, overId) => {
    if (!overId || activeId === overId) return;
    setOrderedIds((prev) => {
      const oldIndex = prev.indexOf(activeId);
      const newIndex = prev.indexOf(overId);
      if (oldIndex < 0 || newIndex < 0) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
    setSequenceSource('manual');
  }, []);

  const applyPlanResult = useCallback((data) => {
    if (typeof data.totalDistanceMeters === 'number') {
      setTotalDistanceMeters(data.totalDistanceMeters);
    } else if (typeof data.totalDistance === 'number') {
      setTotalDistanceMeters(data.totalDistance);
    }
    if (typeof data.totalDurationSeconds === 'number') {
      setTotalDurationSeconds(data.totalDurationSeconds);
    } else if (typeof data.totalDuration === 'number') {
      setTotalDurationSeconds(data.totalDuration);
    } else {
      setTotalDurationSeconds(null);
    }
    setOriginStoreId(data.originStoreId || null);
    setStoreIds(Array.isArray(data.storeIds) ? data.storeIds : []);
    setMultiStore(Boolean(data.multiStore));
    setEstimateError('');
  }, []);

  const runEstimate = useCallback(async (ids, shouldReturn) => {
    if (!ids.length) {
      clearTotals();
      setEstimating(false);
      return;
    }

    const requestId = ++estimateRequestIdRef.current;
    setEstimating(true);
    setEstimateError('');
    try {
      // Dedicated route-planning endpoint. The existing /delivery-routes/optimize is left
      // untouched so the checkbox flow keeps its original behaviour.
      const response = await api.post('/route-planning/estimate', {
        orderIds: ids,
        returnToStore: shouldReturn,
      });
      if (requestId !== estimateRequestIdRef.current) return;
      const data = response.data;
      if (data.success) {
        applyPlanResult(data);
      } else {
        clearTotals();
        setEstimateError(data.message || 'Failed to estimate route');
      }
    } catch (error) {
      if (requestId !== estimateRequestIdRef.current) return;
      const code = error.response?.data?.code;
      const message = error.response?.data?.message || error.message || 'Failed to estimate route';
      clearTotals();
      setEstimateError(message);
      if (code && code !== 'OPTIMIZE_LIMIT_EXCEEDED') {
        // estimate shouldn't hit OPTIMIZE_LIMIT; still avoid raw 400 noise
        console.warn('Route estimate failed', code, message);
      }
    } finally {
      if (requestId === estimateRequestIdRef.current) {
        setEstimating(false);
      }
    }
  }, [applyPlanResult, clearTotals]);

  // Debounced ETA — not one request per drag frame (R6).
  // autoEstimate is on for the map path; the checkbox-path modal opts out until it grows an ETA UI.
  useEffect(() => {
    if (!autoEstimate) return undefined;

    if (estimateTimerRef.current) {
      clearTimeout(estimateTimerRef.current);
    }
    if (skipNextEstimateRef.current) {
      skipNextEstimateRef.current = false;
      return undefined;
    }
    if (orderedIds.length === 0) {
      clearTotals();
      setEstimating(false);
      return undefined;
    }

    estimateTimerRef.current = setTimeout(() => {
      runEstimate(orderedIds, returnToStore);
    }, ESTIMATE_DEBOUNCE_MS);

    return () => {
      if (estimateTimerRef.current) {
        clearTimeout(estimateTimerRef.current);
      }
    };
  }, [orderedIds, returnToStore, runEstimate, clearTotals, autoEstimate]);

  const optimize = useCallback(async () => {
    if (orderedIds.length === 0) {
      notify('No orders to optimize', 'warning');
      return false;
    }
    if (orderedIds.length > ROUTES_API_MAX_INTERMEDIATES || optimizeDisabled) {
      setOptimizeDisabled(true);
      setOptimizeDisabledReason(
        `Optimize is limited to ${ROUTES_API_MAX_INTERMEDIATES} stops (Google Routes API). You can still create the route with this manual sequence.`
      );
      notify(
        `Cannot optimize more than ${ROUTES_API_MAX_INTERMEDIATES} orders. Create the route with the current sequence, or remove stops.`,
        'warning'
      );
      return false;
    }

    setOptimizing(true);
    try {
      const response = await api.post('/route-planning/optimize', {
        orderIds: orderedIds,
        returnToStore,
      });
      const data = response.data;
      if (data.success && Array.isArray(data.optimizedOrderIds)) {
        const deltas = {};
        data.optimizedOrderIds.forEach((id, newIdx) => {
          const oldIdx = orderedIds.indexOf(id);
          deltas[id] = newIdx - oldIdx;
        });
        const anyMoved = Object.values(deltas).some((d) => d !== 0);
        skipNextEstimateRef.current = true;
        estimateRequestIdRef.current += 1;
        setOrderedIds(data.optimizedOrderIds);
        setSequenceSource('optimized');
        applyPlanResult(data);
        if (anyMoved) {
          setMoveDeltas(deltas);
          setTimeout(() => setMoveDeltas({}), 2000);
          notify('Route optimized for minimum travel time', 'success');
        } else {
          notify('Sequence is already optimal', 'info');
        }
        return true;
      }
      notify(data.message || 'Failed to optimize route', 'error');
      return false;
    } catch (error) {
      const code = error.response?.data?.code;
      const message = error.response?.data?.message || error.message || 'Failed to optimize route';
      if (code === 'OPTIMIZE_LIMIT_EXCEEDED') {
        // Keep the manual sequence. Disable only Optimize — route stays creatable.
        setOptimizeDisabled(true);
        setOptimizeDisabledReason(message);
        notify(message, 'warning');
        return false;
      }
      notify(message, 'error');
      return false;
    } finally {
      setOptimizing(false);
    }
  }, [orderedIds, optimizeDisabled, returnToStore, applyPlanResult, notify]);

  const createRoute = useCallback(async () => {
    if (orderedIds.length === 0) {
      notify('No orders to create route', 'warning');
      return null;
    }

    setCreating(true);
    try {
      // Existing endpoint, unchanged contract — it only reads orderIds.
      const response = await api.post('/delivery-routes', {
        orderIds: orderedIds,
      });
      const data = response.data;
      if (data.success) {
        setCreatedRoute(data.route);
        notify('Route created successfully!', 'success');
        return data.route;
      }
      notify(data.message || 'Failed to create route', 'error');
      return null;
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Error creating route';
      notify(message, 'error');
      return null;
    } finally {
      setCreating(false);
    }
  }, [orderedIds, notify]);

  const orderedOrders = useMemo(
    () => orderedIds.map((id) => ordersById[id]).filter(Boolean),
    [orderedIds, ordersById]
  );

  const isInRoute = useCallback(
    (orderId) => orderedIds.includes(orderId),
    [orderedIds]
  );

  const sequenceIndex = useCallback(
    (orderId) => {
      const idx = orderedIds.indexOf(orderId);
      return idx >= 0 ? idx + 1 : null;
    },
    [orderedIds]
  );

  return {
    orderedIds,
    orderedOrders,
    ordersById,
    missingLocationOrders,
    returnToStore,
    setReturnToStore,
    totalDistanceMeters,
    totalDurationSeconds,
    originStoreId,
    storeIds,
    multiStore,
    sequenceSource,
    estimating,
    optimizing,
    creating,
    optimizeDisabled,
    optimizeDisabledReason,
    createdRoute,
    moveDeltas,
    estimateError,
    canOptimize:
      orderedIds.length > 0 &&
      orderedIds.length <= ROUTES_API_MAX_INTERMEDIATES &&
      !optimizeDisabled,
    initFromOrders,
    initFromOrderIds,
    addOrder,
    removeOrder,
    reorder,
    optimize,
    createRoute,
    reset,
    isInRoute,
    sequenceIndex,
  };
}
